export const OTP_CHALLENGE_STATUS = {
  ALREADY_SENT: 'OTP_ALREADY_SENT',
};

export const OTP_AUTH_ERROR_TYPES = {
  LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
  RESEND_COOLDOWN: 'RESEND_COOLDOWN',
  AUTH_IN_PROGRESS: 'AUTH_IN_PROGRESS',
  INVALID_SESSION: 'INVALID_SESSION',
};

export const getOtpChallengeDetail = (entity, fallbackEmail = '') => {
  const challengeParameters = entity?.challengeParameters || {};
  return {
    userEmail: challengeParameters.email || fallbackEmail || '',
    session: entity?.session || '',
    hint: challengeParameters.hint || '',
    status: challengeParameters.status || '',
  };
};

export const isOtpAlreadySent = entity => (
  entity?.challengeParameters?.status === OTP_CHALLENGE_STATUS.ALREADY_SENT
);

export const getApiErrorData = error => error?.response?.data || error?.data || error || {};

export const getApiErrorStatus = error => (
  error?.response?.status || error?.status || getApiErrorData(error)?.responseCode
);

export const getApiErrorEntity = error => getApiErrorData(error)?.entity || null;

export const getOtpActionRequired = error => getApiErrorData(error)?.actionRequired || {};

export const getApiErrorMessage = (error) => {
  const data = getApiErrorData(error);
  return data?.message || data?.exceptionReason || data?.exceptionDescription || '';
};

export const formatOtpRetryAfter = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.ceil(Number(totalSeconds) || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  if (hours === 0 && seconds > 0) {
    parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  }
  return parts.length > 0 ? parts.join(' ') : 'a few seconds';
};

export const getOtpRetryAfterSeconds = (error) => {
  const actionRequired = getOtpActionRequired(error);
  if (Number.isFinite(Number(actionRequired.retryAfterSeconds))) {
    return Math.max(0, Math.ceil(Number(actionRequired.retryAfterSeconds)));
  }
  if (Number.isFinite(Number(actionRequired.retryAfterMillis))) {
    return Math.max(0, Math.ceil(Number(actionRequired.retryAfterMillis) / 1000));
  }
  if (Number.isFinite(Number(actionRequired.resetAt))) {
    return Math.max(0, Math.ceil((Number(actionRequired.resetAt) - Date.now()) / 1000));
  }
  return 0;
};

export const getOtpRetryAfterText = (error, fallbackSeconds = 0) => {
  const actionRequired = getOtpActionRequired(error);
  return actionRequired.retryAfterText || formatOtpRetryAfter(fallbackSeconds);
};

export const getOtpResetAt = (error) => {
  const resetAt = Number(getOtpActionRequired(error)?.resetAt);
  return Number.isFinite(resetAt) && resetAt > 0 ? resetAt : null;
};

export const getOtpAuthErrorType = (error) => {
  const data = getApiErrorData(error);
  const actionRequired = getOtpActionRequired(error);
  const message = `${getApiErrorMessage(error)}`.toLowerCase();
  const responseCode = `${data?.responseCode || ''}`.toLowerCase();
  const rateLimitScope = `${actionRequired.rateLimitScope || ''}`.toLowerCase();
  if (
    message.includes('wait before resending otp')
    || responseCode.includes('resend_cooldown')
    || rateLimitScope.includes('otp_resend_cooldown')
  ) {
    return OTP_AUTH_ERROR_TYPES.RESEND_COOLDOWN;
  }
  if (
    actionRequired.type === 'OTP_RATE_LIMIT'
    || message.includes('otp limit exceeded')
    || responseCode.includes('otp_limit_exceeded')
    || rateLimitScope.includes('otp_send_limit')
  ) {
    return OTP_AUTH_ERROR_TYPES.LIMIT_EXCEEDED;
  }
  if (message.includes('authentication already in progress')) {
    return OTP_AUTH_ERROR_TYPES.AUTH_IN_PROGRESS;
  }
  if (message.includes('invalid session')) {
    return OTP_AUTH_ERROR_TYPES.INVALID_SESSION;
  }
  return '';
};

export const isOtpAuthError = error => Boolean(getOtpAuthErrorType(error));

export const getOtpAuthErrorMessage = (error) => {
  const errorType = getOtpAuthErrorType(error);
  const retryAfterSeconds = getOtpRetryAfterSeconds(error);
  const retryAfterText = retryAfterSeconds > 0
    ? getOtpRetryAfterText(error, retryAfterSeconds)
    : '';
  switch (errorType) {
    case OTP_AUTH_ERROR_TYPES.LIMIT_EXCEEDED:
      return retryAfterText
        ? `You have reached the OTP request limit. Please try again in ${retryAfterText}.`
        : 'You have reached the OTP request limit. Please try again later.';
    case OTP_AUTH_ERROR_TYPES.RESEND_COOLDOWN:
      return retryAfterText
        ? `Please wait ${retryAfterText} before requesting another OTP.`
        : 'Please wait before requesting another OTP.';
    case OTP_AUTH_ERROR_TYPES.AUTH_IN_PROGRESS:
      return 'Authentication is already in progress. Please try again shortly.';
    case OTP_AUTH_ERROR_TYPES.INVALID_SESSION:
      return 'This OTP session is no longer valid. Please sign in again.';
    default:
      return getApiErrorMessage(error);
  }
};
